import { Link } from 'react-router-dom'
import { api, type PublicPlayer } from '../../api/public'
import { Empty, Failed, Loading } from '../../components/Async'
import { useApi } from '../../components/useApi'
import { usePlayerPaths } from '../../components/playerPaths'

/**
 * The roster.
 *
 * The first of SC-010's three interactions: from here a member reaches any
 * player's profile, and from there their trend, record and head-to-head. It is
 * also how a producer discovers player identifiers, which is why it needs no
 * credential (FR-048j).
 */
export function Players() {
  const { pathFor } = usePlayerPaths()
  const players = useApi((s) => api.players(s), [])
  if (players.state === 'loading') return <Loading what="the roster" />
  if (players.state === 'error') return <Failed what="The roster" error={players.error} />

  // Current members first, then former ones, each in the roster's own order.
  const rows = [...players.data].sort((a, b) => Number(b.active) - Number(a.active))

  return (
    <section>
      <h2>Players</h2>
      {rows.length === 0 ? (
        <Empty>The league has no members yet.</Empty>
      ) : (
        <div className="scroll-x">
          <table className="table table-cards players-table" data-testid="players-table">
            <thead>
              <tr>
                <th scope="col">Player</th>
                <th scope="col" className="numeric">
                  Handicap index
                </th>
                <th scope="col">Championships</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.public_id} className={p.active ? undefined : 'player-former'}>
                  <th scope="row">
                    <Link to={pathFor(p.public_id)}>{p.display_name}</Link>
                    {!p.active && <em className="mark"> former member</em>}
                  </th>
                  {/* A decimal string, shown as it arrived. */}
                  <td className="numeric strong" data-testid={`index-${p.public_id}`}>
                    {p.current_index ?? '—'}
                  </td>
                  <Trophies titles={p.championships ?? []} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

/**
 * One trophy per season won. The seasons are named on hover and to a screen
 * reader, which otherwise would hear "trophy trophy".
 */
function Trophies({ titles }: { titles: NonNullable<PublicPlayer['championships']> }) {
  if (titles.length === 0) return <td className="trophies" />
  const seasons = titles.map((t) => t.season_name).join(', ')
  const label = `${titles.length} championship${titles.length === 1 ? '' : 's'}: ${seasons}`
  return (
    <td className="trophies" title={label}>
      <span className="trophy" role="img" aria-label={label}>
        {'🏆'.repeat(titles.length)}
      </span>
    </td>
  )
}
